import { bunja } from 'bunja';
import { CSSProperties } from 'react';

import { GnbRedDotTypes } from '@flipster/apis/v1/red-dot/types';
import { TradingCompetition } from '@flipster/apis/v1/tradingCompetition/types';
import { UserPoolEligibilityResponse } from '@flipster/apis/v1/userpools/types';
import {
  TradingLeaderboardInterval,
  TradingLeaderboardMyRankingItem,
} from '@flipster/apis/v2/trading-leaderboards/types';
import { i18nBunja } from '@flipster/i18n';

export interface Nav {
  isHide?: boolean;
  redDotKey?: GnbRedDotTypes;
}

export interface LeaderboardNav extends Nav {
  type: 'leaderboard';
  label: string;
  url: string;
  ranking: TradingLeaderboardMyRankingItem | undefined;
}

export interface LinkNav extends Nav {
  type: 'link';
  label: string;
  url: string;
  newTab?: boolean;
}

export interface MenuNav extends Nav {
  type: 'menu';
  label: string; // React node
  children: (LinkNav | LeaderboardNav)[];
  style?: CSSProperties;
}

export interface GroupNav extends Nav {
  type: 'group';
  label: string;
  children: MenuNav[];
}

export type Navigations = LinkNav | MenuNav | GroupNav;

export const separateGroup = (navigations: (MenuNav | LinkNav | GroupNav)[]) => {
  return navigations.flatMap((nav) => {
    if (nav.type === 'group') return nav.children;
    return nav;
  });
};

interface GetNavigationParams {
  isLoggedIn?: boolean;
  isSpotOnly?: boolean;
  referrerSymbols?: { perpetual?: string; spot?: string };
  competitions?: TradingCompetition[];
  userPoolEligibility?: UserPoolEligibilityResponse;
  myTradingLeaderboardsRanking?: Record<TradingLeaderboardInterval, TradingLeaderboardMyRankingItem | undefined>;
  enabledFeatureFlags?: string[];
}

// https://prextech.slack.com/archives/C09JA7AT51Q/p1761704629652339?thread_ts=1761703877.574999&cid=C09JA7AT51Q
const copyTradingMasterTraderDueDate = Date.UTC(2025, 11 - 1, 1);

export const navBunja = bunja(() => {
  const { getT } = bunja.use(i18nBunja);

  const t = getT();

  const getNavigation = ({
    isLoggedIn,
    isSpotOnly,
    referrerSymbols,
    competitions = [],
    userPoolEligibility,
    myTradingLeaderboardsRanking,
    enabledFeatureFlags,
  }: GetNavigationParams): Navigations[] => {
    const isEnabledUsd1PointsDashboard = enabledFeatureFlags?.includes('25q3-ti-usd1-points-dashboard');
    const showCopyTraderMasterTrader = Date.now() < copyTradingMasterTraderDueDate;

    return [
      {
        type: 'menu',
        label: t('Navigation.r250401.Trade'),
        isHide: isSpotOnly,
        children: [
          {
            type: 'link',
            label: t('Navigation.r250401.Perpetual'),
            url:
              isLoggedIn && referrerSymbols?.perpetual
                ? `/trade/perpetual/${referrerSymbols.perpetual}`
                : '/trade/perpetual',
          },
          {
            type: 'link',
            label: t('Navigation.r250401.Spot'),
            url: isLoggedIn && referrerSymbols?.spot ? `/trade/spot/${referrerSymbols.spot}` : '/trade/spot',
          },
        ],
      },
      {
        type: 'link',
        label: t('Navigation.r250401.Trade'),
        url: isLoggedIn && referrerSymbols?.spot ? `/trade/spot/${referrerSymbols.spot}` : '/trade/spot',
        isHide: !isSpotOnly,
      },
      {
        type: 'link',
        label: t('Navigation.r250401.Market'),
        url: `/market/${isSpotOnly ? 'spot' : 'perpetual'}`,
      },
      {
        type: 'link',
        label: t('Navigation.r250401.Insights'),
        url: '/insights',
        isHide: isSpotOnly,
      },
      {
        type: 'menu',
        label: t('Navigation.r250401.Earn_plus'),
        isHide: isSpotOnly,
        children: [
          {
            type: 'link',
            label: t('Navigation.r250401.Earn'),
            url: '/earn',
          },
          {
            type: 'link',
            label: t('Navigation.r250829.New_joiner_pool'),
            url: `/userpool/${userPoolEligibility?.id}`,
            isHide: !userPoolEligibility?.eligible,
          },
          {
            type: 'link',
            label: t('Navigation.r250401.Launchpool'),
            url: '/launchpool',
          },
        ],
      },
      {
        type: 'link',
        label: t('Navigation.r250401.Assets'),
        url: '/assets',
        isHide: !isLoggedIn,
      },
      {
        type: 'group',
        label: t('Navigation.r250401.History'),
        isHide: !isLoggedIn,
        children: [
          {
            type: 'menu',
            label: t('Navigation.r250401.Perpetual_history'),
            style: { gridColumn: 1, gridRow: 1 },
            isHide: !isLoggedIn || isSpotOnly,
            children: [
              {
                type: 'link',
                label: t('Navigation.r250401.Position'),
                url: '/history/perpetual/position',
              },
              {
                type: 'link',
                label: t('Navigation.r250401.Pnl'),
                url: '/history/perpetual/pnl',
              },
              {
                type: 'link',
                label: t('Navigation.r250401.Funding'),
                url: '/history/perpetual/funding',
              },
              {
                type: 'link',
                label: t('Navigation.r250401.Trade'),
                url: '/history/perpetual/trade',
              },
              {
                type: 'link',
                label: t('Navigation.r250401.Profit_boost'),
                url: '/history/perpetual/profit-boost',
              },
              { type: 'link', label: t('Navigation.r250401.Airdrop'), url: '/history/perpetual/airdrop' },
            ],
          },
          {
            type: 'menu',
            label: t('Navigation.r250401.Spot_history'),
            style: { gridColumn: 1, gridRow: isSpotOnly ? 1 : 2 },
            isHide: !isLoggedIn,
            children: [
              {
                type: 'link',
                label: t('Navigation.r250401.Pnl'),
                url: '/history/spot/pnl',
              },
              {
                type: 'link',
                label: t('Navigation.r250401.Trade'),
                url: '/history/spot/trade',
              },
            ],
          },
          {
            type: 'menu',
            label: t('Navigation.r250401.Transaction_history'),
            style: { gridColumn: 2, gridRow: '1/3' },
            isHide: !isLoggedIn,
            children: [
              {
                type: 'link',
                label: t('Navigation.r250401.Earn'),
                url: '/history/transaction/earn',
                isHide: isSpotOnly,
              },
              {
                type: 'link',
                label: t('Navigation.r250401.Launchpool_reward'),
                url: '/history/transaction/launchpool-reward',
                isHide: isSpotOnly,
              },
              {
                type: 'link',
                label: t('Navigation.r250401.Deposit'),
                url: '/history/transaction/deposit',
              },
              {
                type: 'link',
                label: t('Navigation.r250401.Withdrawal'),
                url: '/history/transaction/withdrawal',
              },
              {
                type: 'link',
                label: t('Navigation.r250401.Conversion'),
                url: '/history/transaction/conversion',
              },
              {
                type: 'link',
                label: t('Navigation.r250818.Referral_affiliate'),
                url: '/history/transaction/referral-and-affiliate',
                isHide: isSpotOnly,
              },
            ],
          },
        ],
      },
      {
        type: 'group',
        label: t('Navigation.r250401.More'),
        children: [
          {
            type: 'menu',
            label: t('Navigation.r250401.Events'),
            style: { gridColumn: 1, gridRow: 1 },
            isHide: isSpotOnly,
            children: (
              [
                {
                  type: 'link',
                  label: t('Navigation.r250401.Referral_program'),
                  url: '/marketing/referral',
                  newTab: true,
                },
                {
                  type: 'link',
                  label: t('Navigation.r250401.Vip_program'),
                  url: '/marketing/vip',
                  newTab: true,
                },
                {
                  type: 'link',
                  label: t('Navigation.r250401.Affiliate_program'),
                  url: '/marketing/affiliates',
                  newTab: true,
                },
                showCopyTraderMasterTrader && {
                  type: 'link',
                  label: t('Navigation.r250925.Master_program'),
                  url: '/copy-trading/master-trader',
                  newTab: true,
                  redDotKey: 'MASTER_PROGRAM',
                },
              ] satisfies (LeaderboardNav | LinkNav | false)[]
            ).filter(Boolean),
          },
          {
            type: 'menu',
            label: t('Navigation.r250401.Challenges'),
            style: { gridColumn: 1, gridRow: 2 },
            isHide: isSpotOnly,
            children: [
              {
                type: 'link',
                label: t('Navigation.r250401.Sparks_auction'),
                url: '/spark/auction',
              },
              ...(isLoggedIn
                ? competitions.map<LinkNav>((c) => ({
                    type: 'link',
                    label: c.title,
                    url: `/competitions/${c.competitionUrl}`,
                  }))
                : []),
            ],
          },
          {
            type: 'menu',
            label: t('Navigation.r250911.Wlfi_x_flipster_points'),
            style: { gridColumn: 1, gridRow: 3 },
            isHide: !isEnabledUsd1PointsDashboard,
            children: [
              {
                type: 'link',
                label: t('Navigation.r250911.Usd1_points_program'),
                url: '/usd1-points',
              },
            ],
          },
          {
            type: 'menu',
            label: t('Navigation.r250401.Pnl_leaderboard'),
            style: { gridColumn: 2, gridRow: 1 },
            isHide: isSpotOnly,
            children: [
              {
                type: 'leaderboard',
                label: t('Navigation.r250401.Weekly'),
                url: `/trading-leaderboard?interval=WEEKLY&tier=${myTradingLeaderboardsRanking?.WEEKLY?.tier ?? 'ALL'}`,
                ranking: myTradingLeaderboardsRanking?.WEEKLY,
              },
              {
                type: 'leaderboard',
                label: t('Navigation.r250401.Monthly'),
                url: `/trading-leaderboard?interval=MONTHLY&tier=${myTradingLeaderboardsRanking?.MONTHLY?.tier ?? 'ALL'}`,
                ranking: myTradingLeaderboardsRanking?.MONTHLY,
              },
            ],
          },
          {
            type: 'menu',
            label: t('Navigation.r250401.Learn'),
            style: isSpotOnly ? { gridColumn: 1, gridRow: 1 } : { gridColumn: 2, gridRow: 2 },
            children: [
              {
                type: 'link',
                label: t('Navigation.r250401.Blog'),
                url: '/blog',
              },
              {
                type: 'link',
                label: t('Navigation.r250401.Glossary'),
                url: '/glossary',
              },
            ],
          },
        ],
      },
    ];
  };

  return { getNavigation };
});
